import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'
import { nextTick } from 'vue'

const DEFAULT_ANCHOR_OFFSET = 88
const EXTRA_HEADER_GAP = 16

const parseCssPx = (value: string) => {
  const parsed = Number.parseFloat(String(value || '').trim())
  return Number.isFinite(parsed) ? parsed : 0
}

export const getAnchorOffset = () => {
  if (!import.meta.client) {
    return DEFAULT_ANCHOR_OFFSET
  }
  const rootStyle = getComputedStyle(document.documentElement)
  const cssOffset = parseCssPx(rootStyle.getPropertyValue('--anchor-offset'))
  const header = document.querySelector('.header-wrap') as HTMLElement | null
  const headerOffset = Math.round(header?.getBoundingClientRect().height || 0) + EXTRA_HEADER_GAP
  return Math.max(DEFAULT_ANCHOR_OFFSET, cssOffset, headerOffset)
}

export const scrollToElementWithOffset = (target: Element | null, behavior: ScrollBehavior = 'auto') => {
  if (!import.meta.client || !target) {
    return false
  }
  const top = window.scrollY + target.getBoundingClientRect().top - getAnchorOffset()
  window.scrollTo({ top: Math.max(0, top), behavior })
  return true
}

export const scrollToSelectorWithOffset = (selector: string, behavior: ScrollBehavior = 'auto') => {
  if (!import.meta.client || !selector) {
    return false
  }
  return scrollToElementWithOffset(document.querySelector(selector), behavior)
}

export const scrollToPageTop = (behavior: ScrollBehavior = 'auto') => {
  if (!import.meta.client) {
    return
  }
  window.scrollTo({ top: 0, behavior })
}

export const replaceRouteHash = async (router: Router, route: RouteLocationNormalizedLoaded, hash: string) => {
  if (!import.meta.client || !hash || route.hash === hash) {
    return
  }
  await router.replace({
    path: route.path,
    query: { ...route.query },
    hash
  })
}

export const resolveInPageAnchorHrefFromClick = (event: MouseEvent) => {
  if (
    !import.meta.client ||
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return ''
  }
  const target = event.target
  if (!(target instanceof Element)) {
    return ''
  }
  const link = target.closest('a[href^="#"]') as HTMLAnchorElement | null
  if (!link || (link.target && link.target !== '_self')) {
    return ''
  }
  const href = String(link.getAttribute('href') || '')
  return href && href !== '#' ? href : ''
}

export const resolveAnchorIdFromHash = (hash: string) => {
  const raw = String(hash || '').replace(/^#/, '').trim()
  if (!raw) {
    return ''
  }
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

export const resolveCommentIdFromHash = (hash: string) => {
  const match = resolveAnchorIdFromHash(hash).match(/^comment-(\d+)$/)
  return match ? Number(match[1]) : 0
}

export const getAnchorNodeByHash = (hash: string) => {
  if (!import.meta.client) {
    return null
  }
  const id = resolveAnchorIdFromHash(hash)
  if (!id) {
    return null
  }
  return document.getElementById(id)
}

export const waitForAnchorNodeByHash = async (hash: string, retries = 24, delay = 80) => {
  for (let i = 0; i < retries; i += 1) {
    await nextTick()
    if (getAnchorNodeByHash(hash)) {
      return true
    }
    await new Promise(resolve => window.setTimeout(resolve, delay))
  }
  return false
}

type CommentTreeLikeNode = {
  ID?: number
  children?: CommentTreeLikeNode[]
}

export const commentTreeHasId = <T extends CommentTreeLikeNode>(list: T[], targetId: number): boolean =>
  list.some(item => Number(item.ID) === targetId || commentTreeHasId((item.children || []) as T[], targetId))
